import { useRef, useState } from "react";
import { api, ApiError } from "../api/client";
import { ErrorBox } from "../components/States";
import { MeResponse } from "../types";

interface PreviewRow {
  row: number;
  errors: string[];
  deal_date?: string;
  counterparty_name?: string;
  currency?: string;
  amount?: string;
  rate?: string;
  note?: string | null;
  created_deal_id?: number;
}

interface PreviewResp {
  rows: PreviewRow[];
  created: number;
  errors: { row: number; error: string }[];
  dry_run?: boolean;
  snapshot?: string | null;
}

export function ExportImportPage({ me }: { me: MeResponse }) {
  const [preview, setPreview] = useState<PreviewResp | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const isAllowed = me.roles.includes("admin") || me.roles.includes("finance_lead");
  if (!isAllowed) {
    return (
      <div className="text-tg-hint text-sm">
        Раздел доступен админу и финансовому руководителю.
      </div>
    );
  }

  const downloadTemplate = async () => {
    setBusy("template");
    setError(null);
    try {
      const res = await fetch("/api/exports/template", {
        headers: {
          "X-Telegram-Init-Data": (window as any).Telegram?.WebApp?.initData || "",
        },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "export_lots_template.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const runPreview = async () => {
    if (!file) return;
    setBusy("preview");
    setError(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch("/api/exports/preview", {
        method: "POST",
        headers: {
          "X-Telegram-Init-Data": (window as any).Telegram?.WebApp?.initData || "",
        },
        body: fd,
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `HTTP ${res.status}`);
      }
      setPreview(await res.json());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const runImport = async () => {
    if (!file) return;
    if (!confirm(
      "Подтвердить импорт? Перед операцией будет сделан снапшот БД.\n" +
      "Заявки будут заведены как EXPORT с указанными датами.",
    )) return;
    setBusy("import");
    setError(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch("/api/exports/import", {
        method: "POST",
        headers: {
          "X-Telegram-Init-Data": (window as any).Telegram?.WebApp?.initData || "",
        },
        body: fd,
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `HTTP ${res.status}`);
      }
      setPreview(await res.json());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const hasErrors = (preview?.errors?.length || 0) > 0;

  return (
    <div className="space-y-3">
      <h1 className="text-xl font-semibold">📥 Импорт экспортных заявок</h1>
      <p className="text-xs text-tg-hint">
        Используется для массового заведения экспортных лотов задним числом
        (закрытие периода). Скачайте шаблон, заполните, загрузите. Каждое
        заведение делает снимок БД, чтобы можно было откатить.
      </p>

      {error && <ErrorBox text={error} />}

      <section className="bg-tg-sectionBg rounded-xl p-3 space-y-2">
        <div className="text-sm font-medium">1. Скачать шаблон</div>
        <button
          disabled={busy === "template"}
          onClick={downloadTemplate}
          className="w-full py-2 rounded-lg bg-blue-600 text-white text-sm font-medium disabled:opacity-50"
        >
          📥 Скачать Excel-шаблон
        </button>
      </section>

      <section className="bg-tg-sectionBg rounded-xl p-3 space-y-2">
        <div className="text-sm font-medium">2. Загрузить заполненный файл</div>
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="block w-full text-sm"
        />
        {file && (
          <div className="text-xs text-tg-hint">
            Файл: {file.name} ({Math.round(file.size / 1024)} KB)
          </div>
        )}
        <div className="flex gap-2">
          <button
            disabled={!file || busy === "preview"}
            onClick={runPreview}
            className="flex-1 py-2 rounded-lg bg-tg-secondaryBg text-sm disabled:opacity-50"
          >
            👁 Предпросмотр (dry-run)
          </button>
          <button
            disabled={!file || busy === "import" || hasErrors}
            onClick={runImport}
            className="flex-1 py-2 rounded-lg bg-green-600 text-white text-sm font-medium disabled:opacity-50"
          >
            ✅ Импортировать
          </button>
        </div>
      </section>

      {preview && (
        <section className="bg-tg-sectionBg rounded-xl p-3 space-y-2">
          <div className="text-sm font-medium">
            3. Результат{" "}
            {preview.dry_run ? "(предпросмотр)" : `(создано: ${preview.created})`}
          </div>
          {preview.snapshot && (
            <div className="text-[11px] text-tg-hint">
              Снапшот: {preview.snapshot}
            </div>
          )}
          {hasErrors && (
            <div className="bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200 text-xs px-3 py-2 rounded-lg">
              ⚠️ В файле есть ошибки. Импорт не выполнится, пока вы их не
              исправите.
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-tg-hint">
                  <th className="text-left py-1">#</th>
                  <th className="text-left py-1">Дата</th>
                  <th className="text-left py-1">Контрагент</th>
                  <th className="text-left py-1">Валюта</th>
                  <th className="text-right py-1">Сумма</th>
                  <th className="text-right py-1">Курс</th>
                  <th className="text-left py-1">Ошибки</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((r) => (
                  <tr
                    key={r.row}
                    className={
                      r.errors.length
                        ? "bg-red-50 dark:bg-red-900/30"
                        : r.created_deal_id
                        ? "bg-green-50 dark:bg-green-900/30"
                        : ""
                    }
                  >
                    <td className="py-1">{r.row}</td>
                    <td className="py-1">{r.deal_date || "—"}</td>
                    <td className="py-1">{r.counterparty_name || "—"}</td>
                    <td className="py-1">{r.currency || "—"}</td>
                    <td className="py-1 text-right">{r.amount || "—"}</td>
                    <td className="py-1 text-right">{r.rate || "—"}</td>
                    <td className="py-1 text-red-700 dark:text-red-300">
                      {r.errors.join("; ")}
                      {r.created_deal_id && (
                        <span className="text-green-700 dark:text-green-300">
                          ✓ D-{String(r.created_deal_id).padStart(6, "0")}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
